import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Lock, Unlock, Wand2, Plus, Trash2, Save, ListChecks, BookmarkPlus } from 'lucide-react';
import { TemplatePicker } from './TemplatePicker';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { api, errMsg, input, money } from './costingApi';

const AddPartRow = ({ acqId, onAdded }) => {
  const [p, setP] = useState({ name: '', category: '', quantity: 1, selling_price: '' });
  const add = async () => {
    try { await api.post(`/acquisitions/${acqId}/parts`, p); setP({ name: '', category: p.category, quantity: 1, selling_price: '' }); onAdded(); }
    catch (e) { toast.error(errMsg(e, 'Could not add part')); }
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-[2fr_1fr_80px_120px_auto] gap-2" data-testid="add-part-row">
      <Input placeholder="Part name" value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} className={input} data-testid="part-name-input" />
      <Input placeholder="Category" value={p.category} onChange={(e) => setP({ ...p, category: e.target.value })} className={input} />
      <Input type="number" min="1" value={p.quantity} onChange={(e) => setP({ ...p, quantity: e.target.value })} className={input} data-testid="part-qty-input" />
      <Input type="number" step="0.01" placeholder="Sell price" value={p.selling_price} onChange={(e) => setP({ ...p, selling_price: e.target.value })} className={input} data-testid="part-price-input" />
      <Button onClick={add} className="bg-cyan-600 hover:bg-cyan-700" data-testid="add-part-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
    </div>
  );
};

export const AcquisitionDetail = ({ acqId, onClose, onChanged }) => {
  const [d, setD] = useState(null);
  const [edits, setEdits] = useState({});
  const [pickOpen, setPickOpen] = useState(false);
  const load = useCallback(() => api.get(`/acquisitions/${acqId}`).then((x) => { setD(x); setEdits({}); }).catch((e) => toast.error(errMsg(e))), [acqId]);
  useEffect(() => { if (acqId) load(); }, [acqId, load]);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); await load(); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  if (!acqId) return null;
  const locked = d?.allocation_status === 'finalized';
  const a = d?.allocation;

  const saveManual = () => act(() => api.put(`/acquisitions/${acqId}/allocation`, {
    parts: Object.entries(edits).map(([product_id, unit_cost]) => ({ product_id, unit_cost })),
  }), 'Allocation saved (provisional)');
  const reopen = () => { const reason = window.prompt('Reason for reopening this finalised allocation:'); if (reason) act(() => api.post(`/acquisitions/${acqId}/reopen`, { reason }), 'Reopened'); };

  return (
    <Dialog open={!!acqId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl bg-slate-900 border-slate-700 text-white max-h-[92vh] overflow-y-auto" data-testid="acquisition-detail">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {d?.acq_no} · {d?.make} {d?.model} {d?.engine_type && <Badge variant="outline" className="text-slate-200 capitalize">{d.engine_type}</Badge>}
            <Badge className={locked ? 'bg-emerald-700' : 'bg-amber-600'} data-testid="allocation-status">{locked ? 'Finalised' : 'Provisional'}</Badge>
          </DialogTitle>
        </DialogHeader>
        {d && <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            <div className="rounded bg-slate-800 p-2">Acquisition cost<br /><b>{money(a.total_cost)}</b></div>
            <div className="rounded bg-slate-800 p-2">Allocated<br /><b>{money(a.allocated)}</b></div>
            <div className={`rounded p-2 ${a.unallocated ? 'bg-rose-900/60' : 'bg-emerald-900/50'}`} data-testid="unallocated-balance">Unallocated<br /><b>{money(a.unallocated)}</b></div>
            <div className="rounded bg-slate-800 p-2">Funding<br /><b className="capitalize">{(d.funding_source || '').replace('_', ' ')} · {d.payment_status}</b></div>
          </div>
          {!locked && <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setPickOpen(true)} className="bg-sky-700 hover:bg-sky-600" data-testid="start-from-template-btn"><ListChecks className="h-4 w-4 mr-1" />Start from template</Button>
          </div>}
          {d.parts.length > 0 && <Button size="sm" variant="outline" className="text-slate-900 w-fit" data-testid="save-as-template-btn"
            onClick={() => { const name = window.prompt('Template name:', `${d.make || ''} ${d.model || ''} ${d.engine_type || ''} strip`.trim()); if (name) act(() => api.post(`/strip-templates/from-acquisition/${acqId}`, { name }), 'Saved as template'); }}>
            <BookmarkPlus className="h-4 w-4 mr-1" />Save these parts as a template</Button>}
          {!locked && <AddPartRow acqId={acqId} onAdded={() => { load(); onChanged(); }} />}
          <TemplatePicker open={pickOpen} onOpenChange={setPickOpen} acq={d} onApplied={() => { load(); onChanged(); }} />
          <div className="overflow-x-auto border border-slate-700 rounded">
            <table className="w-full text-sm">
              <thead className="bg-slate-800 text-slate-300 text-xs"><tr>
                <th className="p-2 text-left">Part ID</th><th className="p-2 text-left">Part</th><th className="p-2 text-right">Recovered</th><th className="p-2 text-right">On hand</th>
                <th className="p-2 text-right">Sell price</th><th className="p-2 text-right">Unit cost</th><th className="p-2 text-right">Line cost</th><th className="p-2"></th>
              </tr></thead>
              <tbody>{d.parts.map((p) => {
                const unit = edits[p.id] ?? p.allocated_unit_cost;
                return (
                  <tr key={p.id} className="border-t border-slate-700" data-testid={`part-row-${p.id}`}>
                    <td className="p-2 font-mono text-xs">{p.part_no}</td>
                    <td className="p-2">{p.name}<div className="text-[10px] text-slate-400">{p.category}</div></td>
                    <td className="p-2 text-right">{p.recovered_qty}</td>
                    <td className="p-2 text-right">{p.quantity}</td>
                    <td className="p-2 text-right">{money(p.selling_price)}</td>
                    <td className="p-2 text-right">
                      {locked ? money(p.allocated_unit_cost) : (
                        <Input type="number" step="0.01" value={unit} onChange={(e) => setEdits({ ...edits, [p.id]: e.target.value })} className={`${input} h-8 w-28 ml-auto text-right`} data-testid={`unit-cost-${p.id}`} />
                      )}
                    </td>
                    <td className="p-2 text-right">{money((parseFloat(unit) || 0) * p.recovered_qty)}</td>
                    <td className="p-2">{!locked && <Button size="sm" variant="ghost" className="text-rose-400 h-7 px-2" onClick={() => act(() => api.del(`/acquisitions/${acqId}/parts/${p.id}`), 'Part removed')}><Trash2 className="h-3 w-3" /></Button>}</td>
                  </tr>
                );
              })}
              {d.parts.length === 0 && <tr><td colSpan={8} className="p-4 text-center text-slate-400">No parts yet — add the recoverable parts above.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            {!locked ? <>
              <Button onClick={() => act(() => api.post(`/acquisitions/${acqId}/allocation/auto`), 'Cost spread by selling price')} className="bg-indigo-600 hover:bg-indigo-700" data-testid="auto-allocate-btn"><Wand2 className="h-4 w-4 mr-1" />Auto-allocate by selling price</Button>
              <Button onClick={saveManual} disabled={!Object.keys(edits).length} className="bg-slate-600 hover:bg-slate-500" data-testid="save-allocation-btn"><Save className="h-4 w-4 mr-1" />Save edits</Button>
              <Button onClick={() => act(() => api.post(`/acquisitions/${acqId}/finalize`), 'Allocation finalised')} disabled={!a.balanced} className="bg-emerald-600 hover:bg-emerald-700" data-testid="finalize-allocation-btn"><Lock className="h-4 w-4 mr-1" />Finalise</Button>
            </> : <Button onClick={reopen} variant="outline" className="text-slate-900" data-testid="reopen-allocation-btn"><Unlock className="h-4 w-4 mr-1" />Reopen</Button>}
          </div>
        </>}
      </DialogContent>
    </Dialog>
  );
};
